/**
 * <bundle-drawer> — the slide-in flavour picker of the drawer bundle UI (P7).
 *
 * Selection lives here as {variantId: count}; confirm() writes it into the
 * <product-bundle> element's selectedProducts (one entry per unit, live's
 * shape) and triggers its add path. Also serves the standalone
 * [data-standalone-drawer-trigger] buttons rendered by
 * sections/bundle-drawer-trigger-button.liquid (live wired those with an
 * inline script; delegated here instead).
 */
(() => {
  if (customElements.get('bundle-drawer')) return;

  class BundleDrawer extends HTMLElement {
    constructor() {
      super();

      this.drawer = this.querySelector('.bundle-drawer');
      this.overlay = this.querySelector('.bundle-drawer__overlay');
      this.closeButton = this.querySelector('.bundle-drawer__close');
      this.resetButton = this.querySelector('.bundle-drawer__reset');
      this.confirmButton = this.querySelector('.bundle-drawer__confirm');
      this.progressText = this.querySelector('.bundle-drawer__progress-text');
      this.titleElement = this.querySelector('.bundle-drawer__title');
      this.plusButtons = this.querySelectorAll('.bundle-flavor-item__plus');

      this.selectedFlavors = {};
      this.requiredCount = parseInt(this.dataset.requiredCount || 4, 10);

      this.texts = {
        titleSingle: this.dataset.textTitleSingle || 'Wähle deinen Geschmack',
        titleMultiple: this.dataset.textTitleMultiple || 'Wähle Deine {count} Geschmäcker',
        selected: this.dataset.textSelected || 'ausgewählt',
      };

      this.bindEvents();
      this.updateTitle();
      this.updateProgress();
      this.listenForVariantChanges();
    }

    disconnectedCallback() {
      if (this.escapeHandler) {
        document.removeEventListener('keydown', this.escapeHandler);
      }
      if (this.variantChangeHandler) {
        document.removeEventListener('click', this.variantChangeHandler);
      }
    }

    bindEvents() {
      this.closeButton?.addEventListener('click', () => this.close());

      this.addEventListener('click', (event) => {
        if (event.target === this || event.target === this.overlay) {
          this.close();
        }
      });

      this.escapeHandler = (event) => {
        if (event.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      };
      document.addEventListener('keydown', this.escapeHandler);

      this.resetButton?.addEventListener('click', () => this.reset());
      this.confirmButton?.addEventListener('click', () => this.confirm());

      this.querySelectorAll('.bundle-flavor-item').forEach((item) => {
        const plusButton = item.querySelector('.bundle-flavor-item__plus');
        const minusButton = item.querySelector('.bundle-flavor-item__minus');
        const variantId = item.dataset.variantId;

        plusButton?.addEventListener('click', () => this.incrementFlavor(variantId, item));
        minusButton?.addEventListener('click', () => this.decrementFlavor(variantId, item));
      });
    }

    open() {
      this.syncBundleSize();
      this.setAttribute('open', '');
      document.body.classList.add('bundle-drawer-open');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this.drawer.classList.add('bundle-drawer--open');
      });
    }

    syncBundleSize() {
      const checkedRadio = document.querySelector('input[type="radio"][name="refill_bundle"]:checked');
      if (checkedRadio) {
        const newSize = parseInt(checkedRadio.value, 10);
        if (newSize && newSize !== this.requiredCount) {
          this.updateRequiredCount(newSize);
        }
      }
    }

    close() {
      this.drawer.classList.remove('bundle-drawer--open');
      setTimeout(() => {
        this.removeAttribute('open');
        document.body.classList.remove('bundle-drawer-open');
        if (!document.querySelector('cart-drawer.active')) {
          document.body.style.overflow = '';
        }
        this.reset();
      }, 300);
    }

    isOpen() {
      return this.hasAttribute('open');
    }

    cleanProductTitle(title) {
      return title
        .replace('Shakemahlzeit ', '')
        .replace('Balance Shake ', '')
        .replace('sostitutivo del pasto alla ', '')
        .replace('sostitutivo del pasto al ', '')
        .replace('Shake sostitutivo del pasto ', '')
        .replace('sostitutivo del pasto ', '')
        .replace('Cura dimagrante ', '')
        .replace('Abnehmkur ', '');
    }

    parseImageData(data) {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch (error) {
          console.error('Failed to parse images:', error);
          return null;
        }
      }
      return data;
    }

    changeFlavor(variantId, item, increment = true) {
      if (increment) {
        const currentCount = this.getTotalSelectedCount();
        if (currentCount >= this.requiredCount) {
          this.showMaxReachedFeedback();
          return;
        }
        this.selectedFlavors[variantId] = (this.selectedFlavors[variantId] || 0) + 1;
      } else {
        if (!this.selectedFlavors[variantId] || this.selectedFlavors[variantId] <= 0) return;
        this.selectedFlavors[variantId]--;
        if (this.selectedFlavors[variantId] === 0) delete this.selectedFlavors[variantId];
      }
      this.updateFlavorDisplay(variantId, item);
      this.updateProgress();
    }

    incrementFlavor(variantId, item) {
      this.changeFlavor(variantId, item, true);
    }

    decrementFlavor(variantId, item) {
      this.changeFlavor(variantId, item, false);
    }

    updateFlavorDisplay(variantId, item) {
      const quantityDisplay = item.querySelector('.bundle-flavor-item__quantity');
      const minusButton = item.querySelector('.bundle-flavor-item__minus');
      const quantity = this.selectedFlavors[variantId] || 0;

      if (quantityDisplay) {
        quantityDisplay.textContent = quantity;
      }

      if (quantity > 0) {
        item.classList.add('bundle-flavor-item--selected');
        if (minusButton) minusButton.disabled = false;
      } else {
        item.classList.remove('bundle-flavor-item--selected');
        if (minusButton) minusButton.disabled = true;
      }
    }

    getTotalSelectedCount() {
      return Object.values(this.selectedFlavors).reduce((sum, count) => sum + count, 0);
    }

    updateTitle() {
      if (this.titleElement) {
        this.titleElement.textContent = this.requiredCount == 1
          ? this.texts.titleSingle
          : this.texts.titleMultiple.replace('{count}', this.requiredCount);
      }
    }

    updateProgress() {
      const currentCount = this.getTotalSelectedCount();
      const isFull = currentCount >= this.requiredCount;

      if (this.progressText) {
        this.progressText.textContent = `${currentCount}/${this.requiredCount} ${this.texts.selected}`;
      }

      if (this.confirmButton) {
        this.confirmButton.disabled = currentCount !== this.requiredCount;
      }

      this.plusButtons.forEach((plusButton) => (plusButton.disabled = isFull));
    }

    showMaxReachedFeedback() {
      this.progressText?.classList.add('bundle-drawer__progress--max');
      setTimeout(() => {
        this.progressText?.classList.remove('bundle-drawer__progress--max');
      }, 500);
    }

    reset() {
      this.selectedFlavors = {};
      this.querySelectorAll('.bundle-flavor-item').forEach((item) => {
        this.updateFlavorDisplay(item.dataset.variantId, item);
      });
      this.updateProgress();
    }

    confirm() {
      if (this.getTotalSelectedCount() !== this.requiredCount) return;

      document.dispatchEvent(
        new CustomEvent('bundle-drawer:confirmed', {
          bubbles: true,
          detail: { selectedFlavors: this.selectedFlavors },
        })
      );

      const bundleComponent = document.querySelector('product-bundle');
      if (!bundleComponent) return;

      bundleComponent.selectedProducts = [];
      let currentIndex = 0;
      Object.entries(this.selectedFlavors).forEach(([variantId, quantity]) => {
        for (let i = 0; i < quantity; i++) {
          bundleComponent.selectedProducts[currentIndex++] = variantId;
        }
      });

      this.updateBundleDisplay(bundleComponent);
      bundleComponent.updateAddToCartButton();
      bundleComponent.handleAddToCart();
      setTimeout(() => this.close(), 50);
    }

    updateBundleDisplay(bundleComponent) {
      const bundleProducts = bundleComponent.querySelectorAll('.product-bundle__product[data-type="default"]');
      const configuration = bundleComponent.configuration;
      const parsedImages = this.parseImageData(configuration.bundle_products_with_images);
      let currentIndex = 0;

      Object.entries(this.selectedFlavors).forEach(([variantId, quantity]) => {
        const bundleProduct = configuration.bundle_products.find((candidate) => candidate.variants[0].id == variantId);
        if (!bundleProduct) return;

        for (let i = 0; i < quantity; i++) {
          if (currentIndex >= bundleProducts.length) return;
          const targetContainer = bundleProducts[currentIndex];

          this.updateProductImage(targetContainer, bundleProduct, parsedImages);
          this.updateProductTitle(targetContainer, bundleProduct);

          targetContainer.dataset.type = 'selected';
          targetContainer.dataset.productVariantId = variantId;
          currentIndex++;
        }
      });
    }

    updateProductImage(container, bundleProduct, parsedImages) {
      const productImage = container.querySelector('img');
      if (!productImage || !Array.isArray(parsedImages)) return;

      const imageData = parsedImages.find((candidate) => candidate.id == bundleProduct.id);
      if (imageData?.fixed_image_preview_png) {
        productImage.src = imageData.fixed_image_preview_png;
        productImage.classList.remove('default_image');
      }
    }

    updateProductTitle(container, bundleProduct) {
      const selectedProductContainer = container.querySelector('[data-selected-product]');
      if (selectedProductContainer) {
        selectedProductContainer.textContent = this.cleanProductTitle(bundleProduct.title);
      }
    }

    listenForVariantChanges() {
      this.variantChangeHandler = (event) => {
        const radio = event.target.closest('input[type="radio"][name="refill_bundle"]');
        if (radio && radio.checked) {
          const newSize = parseInt(radio.value, 10);
          if (newSize && newSize !== this.requiredCount) {
            this.updateRequiredCount(newSize);
          }
        }
      };
      document.addEventListener('click', this.variantChangeHandler);
    }

    updateRequiredCount(newCount) {
      this.requiredCount = newCount;
      this.dataset.requiredCount = newCount;

      this.updateTitle();

      if (this.getTotalSelectedCount() > newCount) {
        this.reset();
      }
      this.updateProgress();
    }
  }

  customElements.define('bundle-drawer', BundleDrawer);

  // Standalone trigger sections (bundle-drawer-trigger-button) open the same
  // drawer; delegated so triggers rendered anywhere on the page work.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-standalone-drawer-trigger]');
    if (!trigger) return;
    event.preventDefault();
    const drawer = document.querySelector('#BundleDrawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
})();
